/**
 * Thanh thao tác hàng loạt — thay hàng tab Khách hàng/Doanh nghiệp chung khi có hội thoại
 * được tick. Mọi thao tác đi qua một API `POST /conversations/bulk`.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Check, CheckCheck, MailOpen, Tag, Bot, Trash2, X, Loader2, ChevronDown, Building2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useTags } from '@/hooks/use-settings'
import { useThaoTacHangLoat, type HanhDongHangLoat, type PhanLoaiHoiThoai } from '@/hooks/use-conversations'

const NHAN_AI: Record<string, string> = { auto: 'Tự động', suggest: 'Gợi ý', manual: 'Thủ công' }

interface Props {
  ids: string[]
  /** Tab đang xem — thanh chỉ chào nút chuyển sang mục còn lại. */
  phanLoaiHienTai: PhanLoaiHoiThoai
  /** Số dòng đang hiển thị — cho nút "Chọn cả trang". */
  tongTrang: number
  onChonCaTrang: () => void
  onBoChon: () => void
  canDelete: boolean
}

export function ThanhThaoTacHangLoat({ ids, phanLoaiHienTai, tongTrang, onChonCaTrang, onBoChon, canDelete }: Props) {
  const { data: tags = [] } = useTags()
  const bulk = useThaoTacHangLoat()
  const [xacNhanXoa, setXacNhanXoa] = useState(false)
  const [dangLam, setDangLam] = useState<string | null>(null)

  async function chay(action: HanhDongHangLoat, payload?: { tag?: string; aiMode?: string; phanLoai?: PhanLoaiHoiThoai }, nhan?: string) {
    if (ids.length === 0 || dangLam) return
    setDangLam(action)
    try {
      const r = await bulk.mutateAsync({ ids, action, payload })
      const boQua = r.skipped > 0 ? ` (bỏ qua ${r.skipped} ngoài quyền)` : ''
      toast.success(`${nhan ?? 'Đã xử lý'}: ${r.done} hội thoại${boQua}`)
      onBoChon()
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setDangLam(null)
    }
  }

  const ban = !!dangLam

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2 py-1" role="toolbar" aria-label="Thao tác hàng loạt">
        <span className="text-xs font-medium">Đã chọn {ids.length}</span>
        {ids.length < tongTrang && (
          <button type="button" className="text-xs text-primary hover:underline" onClick={onChonCaTrang}>
            Chọn cả trang
          </button>
        )}
        <button type="button" aria-label="Bỏ chọn" className="ml-auto rounded p-0.5 text-muted-foreground hover:bg-muted" onClick={onBoChon}>
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex w-full flex-wrap gap-1">
          <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={ban} onClick={() => chay('mark_read', undefined, 'Đã đánh dấu đã đọc')}>
            {dangLam === 'mark_read' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />} Đã đọc
          </Button>
          <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={ban} onClick={() => chay('mark_unread', undefined, 'Đã đánh dấu chưa đọc')}>
            {dangLam === 'mark_unread' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailOpen className="h-3.5 w-3.5" />} Chưa đọc
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={ban}>
                <Tag className="h-3.5 w-3.5" /> Thẻ <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
              {tags.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">Chưa có thẻ phân loại.</div>}
              {tags.length > 0 && <DropdownMenuLabel className="text-[11px]">Gắn thẻ</DropdownMenuLabel>}
              {tags.map((t) => (
                <DropdownMenuItem key={'add-' + t.id} className="gap-2" onSelect={() => chay('add_tag', { tag: t.name }, `Đã gắn "${t.name}"`)}>
                  <Tag className="h-3.5 w-3.5 shrink-0" style={{ color: t.color, fill: t.color }} />
                  <span className="flex-1 truncate">{t.name}</span>
                </DropdownMenuItem>
              ))}
              {tags.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[11px]">Gỡ thẻ</DropdownMenuLabel>
                  {tags.map((t) => (
                    <DropdownMenuItem key={'rm-' + t.id} className="gap-2" onSelect={() => chay('remove_tag', { tag: t.name }, `Đã gỡ "${t.name}"`)}>
                      <X className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{t.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={ban}>
                <Bot className="h-3.5 w-3.5" /> AI <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuLabel className="text-[11px]">Chế độ AI</DropdownMenuLabel>
              {(['auto', 'suggest', 'manual'] as const).map((m) => (
                <DropdownMenuItem key={m} className="gap-2" onSelect={() => chay('ai_mode', { aiMode: m }, `AI ${NHAN_AI[m].toLowerCase()}`)}>
                  <Check className="h-3.5 w-3.5 opacity-0" />
                  {NHAN_AI[m]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {phanLoaiHienTai === 'noi_bo' ? (
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={ban} onClick={() => chay('phan_loai', { phanLoai: 'khach_hang' }, 'Đã chuyển về Khách hàng')}>
              {dangLam === 'phan_loai' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />} → Khách hàng
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={ban} onClick={() => chay('phan_loai', { phanLoai: 'noi_bo' }, 'Đã chuyển sang Doanh nghiệp chung')}>
              {dangLam === 'phan_loai' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />} → Doanh nghiệp chung
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs text-destructive hover:text-destructive" disabled={ban} onClick={() => setXacNhanXoa(true)}>
              <Trash2 className="h-3.5 w-3.5" /> Xoá
            </Button>
          )}
        </div>
      </div>

      <Dialog open={xacNhanXoa} onOpenChange={(v) => !v && setXacNhanXoa(false)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Xoá {ids.length} hội thoại?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Toàn bộ tin nhắn của {ids.length} hội thoại đã chọn sẽ bị xoá khỏi ChatMQL và không khôi phục được. Tin nhắn trên Zalo của khách không bị ảnh hưởng.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setXacNhanXoa(false)}>Huỷ</Button>
            <Button
              variant="destructive"
              disabled={ban}
              onClick={async () => { setXacNhanXoa(false); await chay('delete', undefined, 'Đã xoá') }}
            >
              Xoá {ids.length} hội thoại
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
